<script setup lang="ts">
import { useSpacePublicPages } from "@/api/public-page-query";
import {
	useDeleteSpaceLogo,
	useSpace,
	useUpdateSpace,
	useUpdateSpaceLogo,
} from "@/api/space-query";
import { useSpaceUsers } from "@/api/user-query";
import AppAvatar from "@/components/common/AppAvatar.vue";
import SettingsFooter from "@/components/common/SettingsFooter.vue";
import CreatePublicPageDialog from "@/components/dialogs/CreatePublicPageDialog.vue";
import CreateUserDialog from "@/components/dialogs/CreateUserDialog.vue";
import FileSelectHelper from "@/components/helpers/FileSelectHelper.vue";
import { icWorkspacesOutline } from "@/components/icons";
import PermissionGuard from "@/components/permission/PermissionGuard.vue";
import PublicPagesTable from "@/components/public-pages/PublicPagesTable.vue";
import SitesSettings from "@/components/spaces/SitesSettings.vue";
import SpaceFeatureFlags from "@/components/spaces/SpaceFeatureFlags.vue";
import StationsSettings from "@/components/spaces/StationsSettings.vue";
import UsersTable from "@/components/users/UsersTable.vue";
import ConfigSection from "@/layouts/content/ConfigSection.vue";
import { useSnackbarStore } from "@/stores";
import { mdiCogOutline } from "@mdi/js";
import { useForm } from "@soft-db/form";
import { Space, SpaceUpdate } from "@soft-db/monitoring-api-client";
import { Button, Divider, FormField, Icon, InputGroup, LinkButton, TextInput } from "@soft-db/ui";
import * as _ from "lodash-es";

const { t } = useI18n();

const route = useRoute();
const id = computed(() => (route.params.id || "") as string);

const snackbar = useSnackbarStore();
const updateSpaceLogo = useUpdateSpaceLogo();
const deleteSpaceLogo = useDeleteSpaceLogo();

async function updateLogo(file: File) {
	await updateSpaceLogo
		.mutateAsync({ space: id.value, image: file })
		.then(snackbar.openOnSaved)
		.catch(snackbar.openOnError);
}
async function deleteLogo() {
	await deleteSpaceLogo
		.mutateAsync({ space: id.value })
		.then(snackbar.openOnSaved)
		.catch(snackbar.openOnError);
}

const { data: space } = useSpace(id);
const updateSpace = useUpdateSpace();

const spaceForm = useForm<SpaceUpdate>();
const name = spaceForm.useField("name", { isRequired: true });
const { data: dashboard } = spaceForm.useField("features.dashboard", {
	isRequired: true,
	initialValue: false,
});
const { data: integrity } = spaceForm.useField("features.integrity", {
	isRequired: true,
	initialValue: false,
});
const { data: report } = spaceForm.useField("features.report", {
	isRequired: true,
	initialValue: false,
});
const { data: audit } = spaceForm.useField("features.audit", {
	isRequired: true,
	initialValue: false,
});

function syncSpace(space: Space) {
	spaceForm.setValues({ name: space.name, features: space.features });
}
whenever(
	space,
	(space) => {
		syncSpace(space);
	},
	{ immediate: true }
);

const router = useRouter();

const { data: users } = useSpaceUsers(id);
const createUserDialog = ref(false);
function openCreateUserDialog() {
	createUserDialog.value = true;
}
function goToUser(userId: string) {
	router.push({ name: "spaces-id-users-userid", params: { id: id.value, userid: userId } });
}

const publicPageDialog = ref(false);
const { publicPages } = useSpacePublicPages(id);

function goToPublicPage(pageId: string) {
	router.push({
		name: "spaces-id-public-pages-pageid",
		params: {
			id: id.value,
			pageid: pageId,
		},
	});
}

const hasChanges = computed(() => {
	if (!space.value) return false;

	return (
		name.data.value !== space.value.name ||
		!_.isEqual(spaceForm.values.value.features, space.value.features)
	);
});

function undoChanges() {
	if (!space.value) return;

	syncSpace(space.value);
}

const saveChanges = spaceForm.handleSubmit(async (data) => {
	if (!space.value) return;

	const updatedSpace = await updateSpace.mutateAsync({
		space: id.value,
		update: {
			name: data.name,
			features: data.features,
		},
	});

	syncSpace(updatedSpace);
	snackbar.openOnSaved();
});
</script>

<template>
	<div class="flex flex-col gap-2">
		<PermissionGuard permission="space.update">
			<ConfigSection :title="t('object.space')" :description="t('space.info')">
				<FileSelectHelper
					class="bg-base-100 flex size-48 items-center justify-center rounded-xl border"
					:showDelete="!!space?.logoUrl"
					@update="updateLogo"
					@delete="deleteLogo"
				>
					<AppAvatar :src="space?.logoUrl" />
				</FileSelectHelper>

				<FormField class="w-full" :label="t('space.name')" :errors="name.errors.value">
					<InputGroup class="w-full">
						<Icon :d="icWorkspacesOutline" />
						<TextInput v-model="name.data.value" />
					</InputGroup>
				</FormField>

				<span class="text-xs">{{ t("space.features") }}</span>

				<SpaceFeatureFlags
					v-model:dashboard="dashboard"
					v-model:integrity="integrity"
					v-model:report="report"
					v-model:audit="audit"
				/>
			</ConfigSection>

			<template #fallback>
				<ConfigSection :title="t('object.space')" :description="t('space.info')">
					<div class="flex items-center gap-4">
						<AppAvatar class="size-16 rounded-xl" :src="space?.logoUrl" />
						<h3>{{ name.data.value }}</h3>
					</div>
				</ConfigSection>
			</template>
		</PermissionGuard>

		<PermissionGuard permission="user.update">
			<Divider />
			<ConfigSection :title="t('object.user', 2)" :description="t('space.userDescription')">
				<UsersTable
					:users="users || []"
					:settingLocation="
						(user) => ({ name: 'spaces-id-users-userid', params: { id, userid: user.id } })
					"
				>
					<template #action>
						<PermissionGuard permission="user.create">
							<Button variant="outline" @click="openCreateUserDialog">
								{{ t("actions.createUser") }}
							</Button>
						</PermissionGuard>
					</template>
				</UsersTable>
			</ConfigSection>
		</PermissionGuard>

		<PermissionGuard permission="public-page.update">
			<Divider />
			<ConfigSection
				:title="t('space.publicPages')"
				:description="t('space.publicPagesDescription')"
			>
				<PublicPagesTable :pages="publicPages || []">
					<template #tableAction>
						<Button variant="outline" @click="publicPageDialog = true">
							{{ t("actions.createPublicPage") }}
						</Button>
					</template>
					<template #rowAction="{ page }">
						<LinkButton
							class="float-right"
							variant="soft"
							size="sm"
							:to="{
								name: 'spaces-id-public-pages-pageid',
								params: { id: id, pageid: page.id },
							}"
						>
							<Icon :d="mdiCogOutline" />
						</LinkButton>
					</template>
				</PublicPagesTable>
			</ConfigSection>
		</PermissionGuard>

		<PermissionGuard permission="space.update">
			<Divider />
			<SitesSettings :id="id" />
		</PermissionGuard>

		<PermissionGuard permission="space.update">
			<Divider />
			<StationsSettings :id="id" />
		</PermissionGuard>

		<PermissionGuard permission="space.update">
			<SettingsFooter
				class="mt-4"
				:hasChanges="hasChanges"
				@save="saveChanges"
				@undo="undoChanges"
			/>
		</PermissionGuard>

		<CreatePublicPageDialog
			v-model:dialog="publicPageDialog"
			:scope="{ type: 'space', id }"
			@create="goToPublicPage"
		/>

		<CreateUserDialog
			v-model:dialog="createUserDialog"
			:scope="{ type: 'space', id }"
			@create="goToUser"
		/>
	</div>
</template>
